<script lang="ts">
  import type { PageProps } from './$types'
  import Artworks from '#lib/components/Artworks.svelte'
  import Banner from '#lib/components/Banner.svelte'
  import StickyNav from '#lib/components/StickyNav.svelte'
  import ArtistCard from '#lib/components/ArtistCard.svelte'
  import Marquee from '#lib/components/Marquee.svelte'
  import SEO from '#lib/components/SEO.svelte'
  import { firstNonEmpty, hasItems } from '#utils/text.js'
  import type { Artist } from '#types'

  let { data }: PageProps = $props()
  const settings = $derived(data.settings)

  const artists = $derived(
    (data.artistsWithArtworks as Artist[]).sort((a, b) => {
      const aCount = a.artworks?.length ?? 0
      const bCount = b.artworks?.length ?? 0
      return bCount - aCount // Sort by most artworks first
    })
  )

  // Transform artists data for StickyNav
  const navItems = $derived(
    artists.map((artist) => ({
      id: artist._id,
      title: artist.name?.replace('Dr.', '') ?? '',
      imageUrl: firstNonEmpty(artist.portrait?.asset?.url) ?? '/static/logo.png' // fallback to logo
    }))
  )

  function processMaterials(materials: string[]): string[] {
    if (materials.length === 0) {
      return []
    }

    // Flatten all materials and split by common separators
    const allMaterials = materials
      .filter(Boolean)
      .flatMap((material) => material.split(/[,; ]/).map((m) => m.trim().toLowerCase()))

    // Remove plurals and normalize
    const normalized = allMaterials.map((material) => {
      // Remove common plural endings
      const normalized = material.replace(/s$/, '')

      // Handle specific cases
      if (normalized === 'charcoal') {
        return 'charcoal'
      }
      if (normalized === 'pastel') {
        return 'pastel'
      }
      if (normalized === 'pigment') {
        return 'pigment'
      }
      if (normalized === 'ink') {
        return 'ink'
      }
      if (normalized === 'paint') {
        return 'paint'
      }
      if (normalized === 'canvas') {
        return 'canvas'
      }
      if (normalized === 'paper') {
        return 'paper'
      }
      if (normalized === 'wood') {
        return 'wood'
      }
      if (normalized === 'metal') {
        return 'metal'
      }
      if (normalized === 'clay') {
        return 'clay'
      }
      if (normalized === 'stone') {
        return 'stone'
      }
      if (normalized === 'fabric') {
        return 'fabric'
      }
      if (normalized === 'thread') {
        return 'thread'
      }
      if (normalized === 'brush') {
        return 'brush'
      }
      if (normalized === 'pencil') {
        return 'pencil'
      }
      if (normalized === 'pen') {
        return 'pen'
      }
      if (normalized === 'marker') {
        return 'marker'
      }
      if (normalized === 'crayon') {
        return 'crayon'
      }
      if (normalized === 'chalk') {
        return 'chalk'
      }
      if (normalized === 'oil') {
        return 'oil'
      }
      if (normalized === 'watercolor') {
        return 'watercolor'
      }
      if (normalized === 'acrylic') {
        return 'acrylic'
      }
      if (normalized === 'tempera') {
        return 'tempera'
      }
      if (normalized === 'gouache') {
        return 'gouache'
      }
      if (normalized === 'spray') {
        return 'spray'
      }
      if (normalized === 'digital') {
        return 'digital'
      }
      if (normalized === 'mixed') {
        return 'mixed media'
      }
      if (normalized === 'collage') {
        return 'collage'
      }
      if (normalized === 'assemblage') {
        return 'assemblage'
      }
      if (normalized === 'sculpture') {
        return 'sculpture'
      }
      if (normalized === 'ceramic') {
        return 'ceramic'
      }
      if (normalized === 'bronze') {
        return 'bronze'
      }
      if (normalized === 'marble') {
        return 'marble'
      }
      if (normalized === 'glass') {
        return 'glass'
      }
      if (normalized === 'textile') {
        return 'textile'
      }
      if (normalized === 'leather') {
        return 'leather'
      }
      if (normalized === 'plastic') {
        return 'plastic'
      }
      if (normalized === 'rubber') {
        return 'rubber'
      }
      if (normalized === 'foam') {
        return 'foam'
      }
      if (normalized === 'cardboard') {
        return 'cardboard'
      }
      if (normalized === 'found') {
        return 'found object'
      }
      if (normalized === 'recycled') {
        return 'recycled material'
      }

      return normalized
    })

    // Remove duplicates and empty strings
    const unique = [...new Set(normalized)].filter((material) => material.length > 0)

    // Sort alphabetically
    return unique.sort()
  }
</script>

<SEO
  title={firstNonEmpty(settings?.seo?.title) ?? 'Art Collection - P.A.P.A'}
  description={firstNonEmpty(settings?.seo?.description) ??
    'Explore our collection of Palestinian art and cultural heritage.'}
  image={settings?.seo?.image}
  type="website"
/>

<div class="relative">
  <!-- Banner Section -->
  <Banner
    className="pb-15"
    title="The PAPA Collection"
    description="Discover our curated collection of Palestinian artworks that preserve and celebrate our rich cultural heritage and artistic traditions."
  />

  <!-- Marquee -->
  <Marquee words={['artists']} speed={0.25} />

  <!-- Sticky Navigation -->
  <StickyNav items={navItems} />

  <!-- Artists and Their Artworks -->
  <section class="py-20">
    <div class="container mx-auto px-5">
      {#each artists as artist, i (artist._id)}
        <div id={artist._id} class="mb-20 pt-5 lg:pt-10">
          <ArtistCard {artist} {processMaterials} priority={i === 0} />

          <!-- Artist's Artworks -->
          {#if hasItems(artist.artworks)}
            <div class="mt-8">
              <Artworks artworks={artist.artworks} title={`${String(artist.name)}'s Artworks`} />
            </div>
          {:else}
            <div class="py-8 text-center">
              <p class="text-black italic">No artworks available for {artist.name}</p>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  </section>
</div>
